import React, { useMemo } from "react";
import { Sparkles, Clock, ShieldAlert, ArrowRight, AlertCircle, FileCheck, Check } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useBridgeStore, bridgeActions, isConsentGiven, TIME_BUDGETS, OBSTACLES, MAX_OBSTACLES, ENGINE_VERSION, type TimeBudgetId, type ObstacleId } from "@/lib/dreamBridge";
import ProgressTracker from "./ProgressTracker";
import WeeklyReflection from "./WeeklyReflection";
import PlanCards from "./PlanCards";
import "./actionPlan.css";
export default function ActionPlan() {
  // CRITICAL RULE: useBridgeStore selectors must return stable references from store
  const bridge = useBridgeStore(s => s.bridge);
  const plan = useBridgeStore(s => s.plan);
  const {
    interpretationDecision,
    timeBudget,
    obstacles,
    progress,
    weeklyReflections,
    includeInDossier
  } = bridge;

  // Memoized checks
  const isDecisionApproved = interpretationDecision === "accepted" || interpretationDecision === "reworded";
  const daysDoneCount = useMemo(() => {
    return progress.daysDone.filter(Boolean).length;
  }, [progress.daysDone]);
  const goalStepsDoneCount = useMemo(() => {
    return progress.goalSteps.reduce((acc, triplet) => {
      return acc + triplet.filter(Boolean).length;
    }, 0);
  }, [progress.goalSteps]);

  // Check consent from storage helper
  const consented = isConsentGiven();

  // Handlers wired to bridgeActions
  const handleTimeBudgetSelect = (id: TimeBudgetId) => {
    bridgeActions.setTimeBudget(id);
  };
  const handleToggleObstacle = (id: ObstacleId) => {
    bridgeActions.toggleObstacle(id);
  };
  const handleToggleGoalStep = (goalIdx: number, stepIdx: number) => {
    bridgeActions.toggleGoalStep(goalIdx, stepIdx);
  };
  const handleToggleDayDone = (dayIdx: number) => {
    bridgeActions.toggleDayDone(dayIdx);
  };
  const handleAddReflection = (text: string, weekLabel?: string) => {
    bridgeActions.addWeeklyReflection(text, weekLabel);
  };
  const handleRemoveReflection = (index: number) => {
    bridgeActions.removeWeeklyReflection(index);
  };
  const handleToggleIncludeInDossier = (checked: boolean) => {
    bridgeActions.setIncludeInDossier(checked);
  };
  return <div id="bridge-plan" className="relative w-full max-w-full space-y-10 py-8 sm:py-12">
      {/* Step Header */}
      <div className="space-y-2">
        <div className="inline-flex items-center gap-2 font-mono text-xs text-primary uppercase tracking-wider font-semibold">
          <span className="size-2 rounded-full bg-primary animate-pulse shrink-0" />
          <span>Step 3 of 4 · Bridge to Reality</span>
        </div>
        <h3 className="font-serif text-2xl sm:text-3xl font-bold text-foreground">
          Grounding Blueprint & Action Plan
        </h3>
        <p className="text-xs sm:text-sm text-muted-foreground max-w-2xl leading-relaxed">
          Convert your inner values into concrete, bite-sized commitments. A seven-day starter
          rhythm, achievable goals, and honest countermeasures to bring your aspirations into your
          living hours.
        </p>
      </div>

      {/* Case 1: Visitor has not approved or reworded an interpretation in Step 2 */}
      {!isDecisionApproved ? <div className="ql-glass p-8 sm:p-12 rounded-2xl border border-border text-center space-y-4 max-w-2xl mx-auto">
          <div className="size-12 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center mx-auto text-primary">
            <Clock className="size-6" />
          </div>
          <h4 className="font-serif text-lg sm:text-xl font-bold text-foreground">
            Complete Step 2 to Generate Your Action Plan
          </h4>
          <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
            Your personalized plan requires a chosen persona and at least one real-world
            aspiration approved in Step 2. Once you accept or reword your interpretation, your
            action plan will generate here automatically.
          </p>
          <div className="pt-2">
            <Link to="#bridge-matters" className="ql-btn min-h-[44px] text-xs font-serif inline-flex items-center gap-2">
              <span>Go to Step 2: Discover What Matters</span>
              <ArrowRight className="size-3.5" />
            </Link>
          </div>
        </div> : (/* Case 2: Interpretation approved. Show configuration & generated plan */
    <div className="space-y-10">
          {/* Configuration Form: Time Budget (required) + Obstacles (optional) */}
          <div className="ql-glass-strong p-5 sm:p-8 rounded-2xl border border-primary/30 space-y-8">
            {/* Time Budget Selector (Required) */}
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                <Label className="text-sm font-semibold text-foreground font-mono">
                  1. Choose Your Realistic Time Budget{" "}
                  <span className="text-primary font-bold">(Required)</span>
                </Label>
                <span className="text-[11px] font-mono text-muted-foreground">
                  {timeBudget ? "Selected" : "Select one to proceed"}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                How much dedicated space can you sustainably protect without overburdening your
                existing schedule?
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-1">
                {TIME_BUDGETS.map(tb => {
              const isSelected = timeBudget === tb.id;
              return <button key={tb.id} type="button" onClick={() => handleTimeBudgetSelect(tb.id)} className={`min-h-[44px] p-4 rounded-xl border text-left transition-all cursor-pointer flex flex-col justify-between space-y-2 min-w-0 ${isSelected ? "bg-primary/10 border-primary ring-1 ring-primary/40 shadow-[0_0_12px_var(--glow-primary)]" : "bg-card/60 border-border hover:border-primary/40"}`}>
                      <div className="flex items-center justify-between gap-2 min-w-0">
                        <span className="font-serif text-sm font-bold text-foreground min-w-0 break-words">
                          {tb.label}
                        </span>
                        {isSelected && <span className="size-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                            <Check className="size-3 stroke-[3]" />
                          </span>}
                      </div>
                      <p className="text-[11px] text-muted-foreground leading-relaxed min-w-0 break-words">
                        {tb.description}
                      </p>
                    </button>;
            })}
              </div>
            </div>

            {/* Obstacle Chips (Optional, max 3) */}
            <div className="space-y-3 border-t border-border/60 pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-semibold text-foreground font-mono">
                    2. Anticipate Friction Points{" "}
                    <span className="text-muted-foreground font-normal">(Optional, up to 3)</span>
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    What tends to get in the way when you attempt real change? If none are chosen,
                    the plan selects default strategic responses.
                  </p>
                </div>
                <span className="font-mono text-xs text-primary font-semibold px-2.5 py-0.5 rounded-full bg-primary/10 border border-primary/30">
                  {obstacles.length} / {MAX_OBSTACLES}
                </span>
              </div>

              <div className="flex flex-wrap gap-2 pt-1">
                {OBSTACLES.map(obs => {
              const isSelected = obstacles.includes(obs.id);
              const isMaxReached = obstacles.length >= MAX_OBSTACLES && !isSelected;
              return <button key={obs.id} type="button" disabled={isMaxReached} title={obs.hint} onClick={() => handleToggleObstacle(obs.id)} data-active={isSelected} className={`ql-chip min-h-[44px] text-xs px-3.5 py-2 ${isMaxReached ? "opacity-35 cursor-not-allowed" : ""}`}>
                      {isSelected && <Check className="size-3.5 shrink-0 stroke-[2.5]" />}
                      <span>{obs.label}</span>
                    </button>;
            })}
              </div>
            </div>
          </div>

          {/* If time budget is not yet chosen, show helpful prompt */}
          {!timeBudget && <div className="p-5 rounded-xl border border-primary/40 bg-card/70 flex items-center gap-3 text-xs text-muted-foreground shadow-md">
              <AlertCircle className="size-4 text-primary shrink-0" />
              <span>
                Please select a time budget above to synthesize your concrete daily schedule,
                milestones, and habit loop.
              </span>
            </div>}

          {/* Render the full plan once time budget is selected and plan is derived */}
          {plan && <div className="space-y-10">
              {/* Progress Summary Tracker */}
              <ProgressTracker daysDoneCount={daysDoneCount} goalStepsDoneCount={goalStepsDoneCount} />

              {/* The Complete Plan Cards */}
              <PlanCards plan={plan} goalSteps={progress.goalSteps} daysDone={progress.daysDone} onToggleGoalStep={handleToggleGoalStep} onToggleDayDone={handleToggleDayDone} />

              {/* Weekly Reflection Journal */}
              <WeeklyReflection reflections={weeklyReflections} isConsented={consented} onAddReflection={handleAddReflection} onRemoveReflection={handleRemoveReflection} />

              {/* Dossier PDF Chapter Integration Switch */}
              <div className="ql-glass p-5 sm:p-7 rounded-2xl border border-primary/40 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="space-y-1.5 max-w-2xl min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileCheck className="size-4 text-primary shrink-0" />
                      <h4 className="font-serif text-base font-bold text-foreground min-w-0 break-words">
                        Include this in my Multiverse Life Dossier PDF
                      </h4>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed min-w-0 break-words">
                      In the Multiverse Life Dossier, this plan appears as a separate
                      chapter called 'From Imagined Self to Lived Reality'. It is practical
                      suggestions, not fiction and not therapy.
                    </p>
                  </div>

                  <div className="flex items-center gap-3 self-start sm:self-auto shrink-0 flex-wrap min-[400px]:flex-nowrap">
                    <Label htmlFor="include-dossier-switch" className="text-xs font-mono text-muted-foreground cursor-pointer min-w-0 break-words select-none">
                      {includeInDossier ? "Included in Dossier" : "Excluded"}
                    </Label>
                    <div className="min-w-[44px] min-h-[44px] flex items-center justify-center">
                      <Switch id="include-dossier-switch" checked={includeInDossier} onCheckedChange={handleToggleIncludeInDossier} aria-label="Include this plan in the Multiverse Life Dossier PDF" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer Engine & Methodology Attribution */}
              <div className="pt-4 border-t border-border/60 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] font-mono text-muted-foreground text-center sm:text-left">
                <div className="flex items-center gap-1.5">
                  <Sparkles className="size-3 text-primary shrink-0" />
                  <span>
                    Plan engine {ENGINE_VERSION} · same answers always give the same plan · written
                    templates, not AI
                  </span>
                </div>
                <span>Optional · Private · Purely Educational</span>
              </div>
            </div>}
        </div>)}
    </div>;
}